import React, { useState, useEffect, FC } from 'react';
import clsx from 'clsx';
import axios from 'axios';
import { useClientEnvVarsStore } from '@marriott/mi-store-utils';
import { Types, Heading, RichText, Button, Link, Messages, InputTextField } from '@marriott/mi-ui-library';
import { getCountryAndLangCode } from '../../modules/utils';
import { useAccountContainerContext } from '../../modules/context/AccountContainerContext';
import { Dropdown } from '../../molecules/Dropdown';
import {
  constants,
  flyoutModalKeys,
  lookupCustomerProfileUrl,
  TRACKING_CONSTANTS,
} from '../../modules/utils/constants/constants';
import { getCustomClickTrackingPageName, maskEmail } from '../../modules/utils/helper';
import { FormData, CountryList, CountryOption, errorType, LookUpMemberNumberProps } from './LookUpMemberNumber.types';
import { FlyOutModalStyle, StyledLookUpMemberNumber } from './LookUpMemberNumber.styles';
import { useAccountModelStore } from '../../modules/store/accountModelStore';
// Use named rather than default exports.
export const LookUpMemberNumber: FC<LookUpMemberNumberProps> = props => {
  const {
    header,
    subHeader,
    countries,
    firstNameLabel,
    lastNameLabel,
    countryRegionLabel,
    emailLabel,
    firstNameError,
    lastNameError,
    emailErrorLabel,
    errorMessageHeader,
    submitButtonLabel,
    cancelButtonLabel,
    apiFailureErrMsg,
    subHeaderEmailSent,
    subHeaderEmailDescription,
    forgotPwrdLabel,
    forgotPwrdCtaPath,
    enablePhaseThree,
    isFlyout,
    accountEvent,
  } = props;

  const { setLookUpEmailSuccess, setActiveKey, setLookUpMemberEmail, setPageErrorReset } = useAccountModelStore(
    state => state
  );

  const clientEnvVars = useClientEnvVarsStore(state => state.envVarsObject);
  const { ACCEPT_LANGUAGE: currentLocaleEnvVar } = clientEnvVars;
  const { currentLocale: contextCurrentLocale } = useAccountContainerContext();
  const currentLocale = contextCurrentLocale || currentLocaleEnvVar || 'en_US';
  const { LOOKUP_MEMBER_SUBMIT, INTERNAL } = TRACKING_CONSTANTS;
  const customClickTrackingPageName = getCustomClickTrackingPageName(accountEvent?.trackingPageName);
  const { countryCode } = getCountryAndLangCode(currentLocale);
  const localeCountryCode = countryCode?.toUpperCase() || constants.USA_COUNTRY_CODE;

  const initialFormData: FormData = {
    email: '',
    firstName: '',
    lastName: '',
    country: {
      id: '',
      value: '',
      countryDialCode: '',
    },
  };

  const [errors, setErrors] = useState({
    firstNameError: '',
    lastNameError: '',
    emailErrorLabel: '',
  });
  const [errorsList, setErrorsList] = useState<string[]>([]);
  const [reverseNamefield, setReverseNamefield] = useState(false);
  const [refererPath, setRefererPath] = useState('');
  const [formData, setFormData] = useState<FormData>(initialFormData);
  const [emailSent, setEmailSent] = useState(false);
  const [apiError, setApiError] = useState(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  useEffect(() => {
    if (countries) {
      if (countries && countries.length) {
        const defCountry = localeCountryCode
          ? countries.find((country: CountryOption) => country?.id?.toUpperCase() === localeCountryCode?.toUpperCase())
          : countries[0];
        setFormData({ ...formData, country: defCountry });
      }
    }
  }, [localeCountryCode]);

  useEffect(() => {
    const allowedCountryCodes = [
      constants.KOREA_COUNTRY_CODE,
      constants.CHINA_COUNTRY_CODE,
      constants.JAPAN_COUNTRY_CODE,
    ];

    if (allowedCountryCodes.includes(localeCountryCode)) {
      setReverseNamefield(true);
    } else {
      setReverseNamefield(false);
    }

    if (typeof window !== 'undefined' && window.document?.referrer) {
      setRefererPath(window.document?.referrer);
    }
  }, []);

  useEffect(() => {
    if (errorsList?.length || apiError || emailSent) window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [errorsList, apiError, emailSent]);

  const handleCountryNameChange = (option: CountryList) => {
    if (
      option.id?.toUpperCase() === constants.KOREA_COUNTRY_CODE ||
      option.id?.toUpperCase() === constants.CHINA_COUNTRY_CODE ||
      option.id?.toUpperCase() === constants.JAPAN_COUNTRY_CODE
    ) {
      setReverseNamefield(true);
    } else {
      setReverseNamefield(false);
    }
    setFormData({ ...formData, country: option });
  };

  const resetForm = (fieldName: string) => {
    const newErrors: errorType = { ...errors };
    switch (fieldName) {
      case 'email':
        newErrors.emailErrorLabel = '';
        break;
      case 'firstName':
        newErrors.firstNameError = '';
        break;
      case 'lastName':
        newErrors.lastNameError = '';
        break;
      default:
    }
    setErrors(newErrors);
  };

  const handleKeyPress = (ev: React.KeyboardEvent<HTMLFormElement>) => {
    ev.preventDefault();
    if (ev.key === 'Enter' || ev.keyCode === 13) {
      handleSubmit(ev);
    }
  };

  const validateAllFields = () => {
    const newErrors: errorType = {
      firstNameError: '',
      lastNameError: '',
      emailErrorLabel: '',
    };
    let isValid = true;
    if (!formData?.email) {
      newErrors.emailErrorLabel = emailErrorLabel;
      isValid = false;
    }
    if (!formData?.firstName) {
      newErrors.firstNameError = firstNameError;
      isValid = false;
    }
    if (!formData?.lastName) {
      newErrors.lastNameError = lastNameError;
      isValid = false;
    }
    setErrors(newErrors);
    const filteredErrors = Object.values(newErrors).filter(value => value !== '');
    setErrorsList(filteredErrors);

    if (isValid) {
      validateLookupMember();
    }
    return isValid;
  };

  const handleSubmit = (ev: React.KeyboardEvent<HTMLFormElement>) => {
    ev.preventDefault();
    setApiError(false);
    validateAllFields();
  };

  // Server Side call
  const validateLookupMember = async () => {
    setIsLoading(true);
    try {
      const { data } = await axios.post(lookupCustomerProfileUrl, {
        firstName: formData?.firstName?.trim(),
        lastName: formData?.lastName?.trim(),
        email: formData?.email?.trim(),
        countryCode: formData?.country?.id?.trim(),
        sendEmail: true,
      });

      if (data?.status === 'SUCCESS') {
        setEmailSent(true);
        if (enablePhaseThree) {
          setLookUpMemberEmail(formData?.email ?? '');
          setActiveKey(flyoutModalKeys.SIGN_IN);
          setLookUpEmailSuccess(true);
          setPageErrorReset(true);
        }
      } else {
        setApiError(true);
      }
    } catch {
      setApiError(true);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <StyledLookUpMemberNumber data-component-name="o-account-lookupmembernumber" data-testid="lookupmembernumber">
      <div className="lookup-member-number container">
        <div
          className={enablePhaseThree ? '' : 'row justify-content-center justify-content-lg-start pt-5 pb-2 pb-md-5'}
        >
          <div className={enablePhaseThree ? '' : 'col-12 col-md-8 col-lg-5 pt-md-3'}>
            {enablePhaseThree ? (
              <Heading variation={Types.headingType.subtitle} fontSize={Types.size.extraLarge} titleText={header} />
            ) : (
              <Heading variation={Types.headingType.title} fontSize={Types.size.small} titleText={header} />
            )}

            {emailSent ? (
              <div>
                <Heading
                  variation={Types.headingType.subtitle}
                  fontSize={Types.size.large}
                  titleText={subHeaderEmailSent}
                  customClass={'pt-4'}
                />

                <RichText
                  text={subHeaderEmailDescription?.replace('{0}', maskEmail(formData?.email?.trim()))}
                  customClass={'px-0 pb-0'}
                  styleclass={'t-font-m'}
                  componentId="lookup-member-number-subHeader"
                />

                <Link
                  text={forgotPwrdLabel}
                  linkClassName="t-font-s mt-5 pt-md-3 link-underline"
                  linkHref={forgotPwrdCtaPath}
                  target="_self"
                  linkType="internal"
                />
              </div>
            ) : (
              <div>
                <RichText
                  text={subHeader}
                  customClass={'px-0 pb-0'}
                  styleclass={'t-font-m'}
                  componentId="lookup-member-number-subHeader"
                />

                {(errorsList?.length || apiError) && (
                  <div>
                    <Messages messageType="error-sev1" className="my-5 account-page-error-msg px-2">
                      {
                        <>
                          <span>{errorMessageHeader}</span>

                          <ul>
                            {apiError ? (
                              <li>
                                <RichText text={apiFailureErrMsg} componentId={apiFailureErrMsg} />
                              </li>
                            ) : (
                              errorsList.map((error, index) => (
                                <li key={index}>
                                  <RichText text={error} componentId={error} />
                                </li>
                              ))
                            )}
                          </ul>
                        </>
                      }
                    </Messages>
                  </div>
                )}

                <form method="post" onSubmit={handleSubmit} data-testid="lookup-form">
                  <InputTextField
                    label={emailLabel}
                    inputValue={formData?.email}
                    testId="email-label"
                    showErrorMessage={!!errors?.emailErrorLabel}
                    messageToShow={emailErrorLabel}
                    messageClass="error-label message"
                    inputMaxLength={80}
                    infoLabel={emailLabel}
                    getInputProps={() => ({
                      autoComplete: 'off',
                      id: `input-Email`,
                      onKeyUp: handleKeyPress,
                    })}
                    getLabelProps={() => ({
                      htmlFor: `input-Email`,
                    })}
                    className={clsx('m-input-text-field-v3 mt-5', formData?.email ? 'is-active' : '')}
                    getInputValue={(val: string) => {
                      val && resetForm('email');
                      setFormData({ ...formData, email: val });
                    }}
                  />

                  {reverseNamefield ? (
                    <InputTextField
                      label={lastNameLabel}
                      inputValue={formData?.lastName}
                      showErrorMessage={!!errors?.lastNameError}
                      messageToShow={errors?.lastNameError ? lastNameError : ''}
                      messageClass="error-label message"
                      inputMaxLength={25}
                      getInputProps={() => ({
                        autoComplete: 'off',
                        id: `input-lastName`,
                        onKeyUp: handleKeyPress,
                      })}
                      getLabelProps={() => ({
                        htmlFor: `input-lastName`,
                      })}
                      className={clsx('m-input-text-field-v3 mt-5', formData?.lastName ? 'is-active' : '')}
                      getInputValue={(val: string) => {
                        val && resetForm('lastName');
                        setFormData({ ...formData, lastName: val });
                      }}
                    />
                  ) : (
                    <InputTextField
                      label={firstNameLabel}
                      inputValue={formData?.firstName}
                      showErrorMessage={!!errors?.firstNameError}
                      messageToShow={errors?.firstNameError ? firstNameError : ''}
                      messageClass="error-label message"
                      inputMaxLength={20}
                      getInputProps={() => ({
                        autoComplete: 'off',
                        id: `input-firstName`,
                        onKeyUp: handleKeyPress,
                      })}
                      getLabelProps={() => ({
                        htmlFor: `input-firstName`,
                      })}
                      className={clsx('m-input-text-field-v3 mt-5', formData?.firstName ? 'is-active' : '')}
                      getInputValue={(val: string) => {
                        val && resetForm('firstName');
                        setFormData({ ...formData, firstName: val });
                      }}
                    />
                  )}

                  {reverseNamefield ? (
                    <InputTextField
                      label={firstNameLabel}
                      inputValue={formData?.firstName}
                      showErrorMessage={!!errors?.firstNameError}
                      messageToShow={errors?.firstNameError ? firstNameError : ''}
                      messageClass="error-label message"
                      inputMaxLength={20}
                      getInputProps={() => ({
                        autoComplete: 'off',
                        id: `input-firstName`,
                        onKeyUp: handleKeyPress,
                      })}
                      getLabelProps={() => ({
                        htmlFor: `input-firstName`,
                      })}
                      className={clsx('m-input-text-field-v3 mt-5', formData?.firstName ? 'is-active' : '')}
                      getInputValue={(val: string) => {
                        val && resetForm('firstName');
                        setFormData({ ...formData, firstName: val });
                      }}
                    />
                  ) : (
                    <InputTextField
                      label={lastNameLabel}
                      inputValue={formData?.lastName}
                      showErrorMessage={!!errors?.lastNameError}
                      messageToShow={errors?.lastNameError ? lastNameError : ''}
                      messageClass="error-label message"
                      inputMaxLength={25}
                      getInputProps={() => ({
                        autoComplete: 'off',
                        id: `input-lastName`,
                        onKeyUp: handleKeyPress,
                      })}
                      getLabelProps={() => ({
                        htmlFor: `input-lastName`,
                      })}
                      className={clsx('m-input-text-field-v3 mt-5', formData?.lastName ? 'is-active' : '')}
                      getInputValue={(val: string) => {
                        val && resetForm('lastName');
                        setFormData({ ...formData, lastName: val });
                      }}
                    />
                  )}

                  <div className="m-input-text-field pb-2 mt-5">
                    <Dropdown
                      defaultOption={formData?.country?.value || ''}
                      dropdownOptions={countries || []}
                      onChange={handleCountryNameChange}
                      customClassName="lookup-countrydropdown is-active"
                      dropdownId="fp-country-code"
                      hasLabel={countryRegionLabel}
                    />
                  </div>

                  <div className="mx-auto d-flex align-items-center flex-column flex-md-row pt-4 px-0 space-bottom submit_btn-wrapper">
                    <Button
                      className={'m-button-m m-button-primary custom-btn submit_btn px-4 py-2 lookup-submit-mobile'}
                      type={Types.ButtonTypeVariation.Submit}
                      testId={'submitButtonLabel'}
                      {...(isFlyout && {
                        custom_click_track_value: isFlyout
                          ? `${customClickTrackingPageName}|${LOOKUP_MEMBER_SUBMIT}|${INTERNAL}|`
                          : '',
                      })}
                      ariaLabel={submitButtonLabel}
                    >
                      {isLoading ? (
                        <div className="m-spinner-s" data-testid="loading-spinner"></div>
                      ) : (
                        <div>{submitButtonLabel}</div>
                      )}
                    </Button>
                    {!enablePhaseThree && (
                      <Link
                        skipFlyout={true}
                        text={cancelButtonLabel}
                        linkClassName="t-font-m mt-4 mt-md-0 mx-md-4 link-underline"
                        linkHref={refererPath ? refererPath : forgotPwrdCtaPath}
                        target="_self"
                        linkType="internal"
                      />
                    )}
                  </div>
                </form>
              </div>
            )}
          </div>
        </div>
      </div>
      {enablePhaseThree && <FlyOutModalStyle />}
    </StyledLookUpMemberNumber>
  );
};
